/**
 * Entry point for structure extraction, plus the event stream the client uses to refetch while it
 * runs. The extraction work itself lives in `api/pipeline/` as independent, DB-in/DB-out steps.
 *
 * Note what this mutation does NOT do any more: it used to start a single long-lived function that ran
 * every step and blocked in the middle on an in-memory listener until the teacher confirmed the
 * structure. Now it runs the structure step only. Whatever depends on a confirmed structure is
 * triggered by the confirmation itself, so nothing is lost if the server restarts in between.
 */

import { logger } from '@adornis/base/logging.js';
import { registerMutation, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import '../../i18n.js';
import { getProgressByExam, StepState } from '../extraction-progress.js';
import { dispatchJobs } from './jobs/dispatch.js';

export const structureExtractionEvents$ = new Subject<{
  examID: string;
  event: string;
  result?: string;
  error?: string;
  message?: any;
  hasLowConfidence?: boolean;
}>();

export const startStructureExtractionSubscription = registerSubscription({
  operationName: 'startStructureExtractionSubscription',
  type: () => String,
  params: [
    { name: 'examID', type: () => String },
    { name: 'conversationId', type: () => String },
  ],
  resolve: (examID: string, conversationId: string) => () =>
    structureExtractionEvents$.pipe(
      filter(e => e.examID === examID),
      map(e => JSON.stringify(e)),
    ),
});

export const startStructureExtractionMutation = registerMutation({
  operationName: 'startStructureExtractionMutation',
  type: () => String,
  params: [
    { name: 'examID', type: () => String },
    { name: 'conversationId', type: () => String },
  ],
  resolve: (examID: string, conversationId: string) => async () => {
    const progress = await getProgressByExam(examID, 'task');
    if (progress && (progress.status === StepState.IN_PROGRESS || progress.status === StepState.COMPLETED)) {
      logger.info({ examID }, 'Strukturerkennung fuer exam laeuft bereits oder ist fertig. Kein Neustart.');
      return 'Already running';
    }

    logger.info({ examID }, 'Queueing structure extraction for exam');
    structureExtractionEvents$.next({ examID, event: 'started' });

    // Queued rather than run here. The step used to be started detached from this mutation, which meant a
    // restart while it ran left the exam with an IN_PROGRESS task extraction and nothing to finish it; as a
    // job it carries a lease, so the sweeper reclaims and retries it.
    //
    // The events the client listens on are emitted by the HANDLER instead (see `structure.job.ts`), because
    // that is where the step's result — the confirmation message the canvas opens on — actually exists.
    // In-process subjects only reach subscribers on THIS instance, which is a pre-existing property of this
    // subscription and not something the queue changes: with more than one instance the client must already
    // be connected to the one doing the work.
    // Nothing here touches the progress record beyond stamping which pass the row belongs to —
    // `runStructureStep` still creates and drives it. See the `structure` type's own `batch`.
    await dispatchJobs('structure', examID, conversationId ? { conversationId } : {});

    // Return immediately so the browser isn't left loading.
    return 'Extraction started in background';
  },
});
